import { SystemIcons } from '@almbrand/systemicons';
import classNames from 'classnames';
import { StepIndicatorDimensions } from 'constants/stepIndicatorDimensions';
import { useBrandSelector } from 'hooks/useBrandSelector';
import React, { useEffect, useState } from 'react';
import styles from './StepIndicator.module.scss';

export interface StepIndicatorProps {
	className?: string;
	currentStepIndex: number;
	longSteps: number[];
	steps: { value: string; title: string; index?: number | null }[];
	getLiIndex: (
		index: number | null | undefined,
		event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
	) => void;
}

export const StepIndicator: React.FC<StepIndicatorProps> = React.memo(function StepIndicator({
	className,
	currentStepIndex,
	steps,
	getLiIndex,
	longSteps,
}) {
	const { theme } = useBrandSelector();
	const [windowDimensions, setWindowDimensions] = useState({ width: window.innerWidth, height: window.innerHeight });

	const getVerticalLineClass = (index: number): string =>
		longSteps.includes(index) ? styles.StepIndicator_verticalLine_long : styles.StepIndicator_verticalLine;

	useEffect(() => {
		const handleResize = () => {
			setWindowDimensions({ width: window.innerWidth, height: window.innerHeight });
		};
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	return (
		<div className={classNames(styles.StepIndicator, className)}>
			{windowDimensions.width > StepIndicatorDimensions.StepIndicatorWidthDesktop &&
			windowDimensions.height > StepIndicatorDimensions.StepIndicatorHeightDesktop ? (
				<ol className={classNames(styles.StepIndicator_list, styles.StepIndicator___desktop)}>
					{steps?.map((step, index) => (
						<div
							style={{ height: longSteps.includes(index) ? 242 : 121 }}
							key={step.value}
						>
							<li
								className={classNames(
									styles.StepIndicator_item,
									currentStepIndex > index && styles.StepIndicator_item___complete,
									currentStepIndex === index && styles.StepIndicator_item___current,
									currentStepIndex < index && styles.StepIndicator_item___disabled
								)}
								aria-current={currentStepIndex === index && 'step'}
								onClick={(e) => getLiIndex(step.index, e)}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										getLiIndex(step.index, e);
									}
								}}
							>
								{currentStepIndex >= index ? (
									<span className={styles.StepIndicator_itemCircle}>
										<SystemIcons
											className={styles.StepIndicator_itemCheckMark}
											themeName={theme}
											icon='Checkmark_with_stroke'
											height={34} // corresponds to 20px(?)
											width={34}
										/>
									</span>
								) : (
									<span className={styles.StepIndicator_itemCircle_disabled} />
								)}
								<p className={styles.StepIndicator_itemText}>{step.title}</p>
							</li>

							{index !== steps.length - 1 && (
								<div
									className={classNames(currentStepIndex > index && getVerticalLineClass(index))}
									data-index={index}
								/>
							)}
						</div>
					))}
				</ol>
			) : (
				<ol className={classNames(styles.StepIndicator_list_mobile, styles.StepIndicator___mobile)}>
					{steps?.map((step, index) => (
						<li
							key={step.value}
							aria-current={currentStepIndex === index && 'step'}
							onClick={(e) => getLiIndex(step.index, e)}
							className={classNames(
								styles.StepIndicator_item_mobile,
								index === 2 && styles.StepIndicator_item_mobile___long
							)}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									getLiIndex(step.index, e);
								}
							}}
						>
							<div
								className={classNames(
									currentStepIndex > index && styles.StepIndicator_item_mobile___complete,
									currentStepIndex === index && styles.StepIndicator_item_mobile___current
								)}
							/>
						</li>
					))}
				</ol>
			)}
		</div>
	);
});
